import { useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  documentId,
} from "firebase/firestore";
import { UFs } from "../../util/UFs";

const db = getFirestore();

/**
 * Teto da lista de motoristas do select. Homologação tem 60; em produção pode ser milhar, e um
 * select com milhar de opções não é usável de qualquer forma. Acima disso o operador filtra pela
 * placa, que resolve o mesmo problema por outro caminho.
 */
const TETO_MOTORISTAS = 300;

/** Teto da frota no select. Homologação tem 50 veículos cadastrados. */
const TETO_PLACAS = 300;

/**
 * A mesma placa está cadastrada em grafias diferentes: em 46 veículos de homologação com placa são
 * 31 distintas, entre `ABC-1234`, `ABC1D23` e minúscula. Sem isto o select repetiria a mesma placa.
 * O backend normaliza de novo o que recebe, então o valor pode ir daqui já limpo.
 */
function normalizarPlaca(bruta) {
  return String(bruta ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

export const OPCOES_DE_UF = Object.entries(UFs).map(([nome, sigla]) => ({
  value: sigla,
  label: `${sigla} — ${nome}`,
}));

export const OPCOES_DE_TIPO = [
  { value: "PROPRIO", label: "Próprio" },
  { value: "TRANSPORTADORA", label: "Transportadora" },
];

/** Carrega uma vez o que os selects de embarcador, motorista e placa precisam. */
export default function useOpcoesDeFiltro(escopo) {
  const [embarcadores, setEmbarcadores] = useState([]);
  const [motoristas, setMotoristas] = useState([]);
  const [placas, setPlacas] = useState([]);

  useEffect(() => {
    let cancelado = false;

    async function carregar() {
      try {
        // Sem viewALL, o select só oferece os embarcadores que o usuário já enxerga — a lista do
        // filtro não pode ser maior que o escopo dele.
        const consultaEmbarcadores = escopo.viewALL
          ? query(collection(db, "shipper"), limit(200))
          : escopo.uids.length
            ? query(
                collection(db, "shipper"),
                where(documentId(), "in", escopo.uids.slice(0, 30)),
              )
            : null;

        // A placa sai do cadastro de veículos, e não das posições: o operador precisa achar no
        // select o veículo que ainda não capturou nada no formato novo. `uidShipper` está em 50 de
        // 50 veículos de homologação, então o escopo do embarcador vale aqui como vale acima.
        const consultaPlacas = escopo.viewALL
          ? query(collection(db, "vehicle"), limit(TETO_PLACAS))
          : escopo.uids.length
            ? query(
                collection(db, "vehicle"),
                where("uidShipper", "in", escopo.uids.slice(0, 30)),
              )
            : null;

        const [snapEmbarcadores, snapMotoristas, snapPlacas] = await Promise.all([
          consultaEmbarcadores ? getDocs(consultaEmbarcadores) : null,
          getDocs(
            query(
              collection(db, "drivers_users"),
              orderBy("name"),
              limit(TETO_MOTORISTAS),
            ),
          ),
          consultaPlacas ? getDocs(consultaPlacas) : null,
        ]);

        if (cancelado) return;

        setEmbarcadores(
          (snapEmbarcadores?.docs ?? [])
            .map((doc) => {
              // O nome do embarcador está em `dataPersonal`: `name` na raiz não existe em nenhum
              // dos 37 de homologação, e o select acabava listando uid.
              const pessoais = doc.data()?.dataPersonal;
              const nome = pessoais?.nameFantasy || pessoais?.socialName;
              return { value: doc.id, label: nome || doc.id };
            })
            .sort((a, b) => a.label.localeCompare(b.label)),
        );
        setMotoristas(
          snapMotoristas.docs
            .filter((doc) => String(doc.data()?.name || "").trim())
            .map((doc) => ({ value: doc.id, label: doc.data().name })),
        );

        // Uma entrada por placa; a primeira grafia encontrada é a que aparece, só em caixa alta,
        // porque há placa cadastrada em minúscula e no select isso lê como defeito.
        const porPlaca = new Map();
        for (const doc of snapPlacas?.docs ?? []) {
          const cadastrada = String(doc.data()?.vehiclePlate || "").trim();
          const normalizada = normalizarPlaca(cadastrada);
          if (!normalizada || porPlaca.has(normalizada)) continue;
          porPlaca.set(normalizada, cadastrada.toUpperCase());
        }
        setPlacas(
          [...porPlaca.entries()]
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        );
      } catch (erro) {
        if (cancelado) return;
        console.error("Erro ao carregar as opções dos filtros:", erro);
      }
    }

    carregar();
    return () => {
      cancelado = true;
    };
  }, [escopo]);

  return { embarcadores, motoristas, placas };
}
